import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Sparkles, ArrowRight, Star, Users, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import HeroCouponPromo from '../HeroCouponPromo';
import { CALL_TEL } from '../../lib/contact';
import { useSeasonalTheme } from '../../contexts/SeasonalThemeContext';

export default function Hero({ onBookService, onFreeInspection }) {
  const [mounted, setMounted] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const navigate = useNavigate();
  const { theme } = useSeasonalTheme();
  const heroCopy = theme?.hero || {};

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let active = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/hero-special-offer`)
      .then((res) => { if (active) setCampaign(res.data); })
      .catch(() => { if (active) setCampaign({ enabled: false }); });
    return () => { active = false; };
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-gradient-to-br from-brand-blue via-brand-blue-light to-brand-blue-dark text-white"
    >
      {theme?.decorations_enabled && theme?.tokens?.particle !== 'none' && <SeasonalParticles type={theme.tokens.particle} />}
      {/* Decorative blurred circles */}
      <div aria-hidden className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-green/30 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-blue-400/20 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-12 pb-20 md:pt-20 md:pb-28 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Copy block */}
          <div className={`lg:col-span-12 text-center lg:text-left transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span
              className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3.5 py-1.5 text-xs sm:text-sm font-medium text-white/90"
              data-testid="hero-eyebrow"
            >
              <Sparkles size={14} className="text-brand-green-light" />
              India&apos;s Most Trusted Pest Control Experts
            </span>

            <h1
              className="mt-5 font-bold leading-[1.05] tracking-tight text-2xl sm:text-4xl lg:text-[52px]"
              style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
              data-testid="hero-title"
            >
              {heroCopy.headline || 'Protect Your Home from Pests — '}
              {!heroCopy.headline && <span className="text-brand-green-light">Safe, Fast &amp; Reliable</span>}
            </h1>

            <p
              className="mt-4 sm:mt-5 text-base sm:text-lg text-white/80 max-w-xl mx-auto lg:mx-0 leading-relaxed"
              data-testid="hero-subtitle"
            >
              {heroCopy.subtitle || 'Professional pest control services using safe and eco-friendly solutions. Trusted by 10,000+ families across Uttarakhand.'}
            </p>

            {/* CTAs */}
            <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center justify-center lg:justify-start">
              <Button
                onClick={onBookService}
                data-testid="hero-book-btn"
                className="h-12 sm:h-14 px-7 text-base font-semibold rounded-full bg-brand-green hover:bg-brand-green-dark text-white shadow-lg shadow-brand-green/20 transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                {heroCopy.cta_primary || 'Book Service Now'}
                <ArrowRight className="ml-2" size={18} />
              </Button>
              {campaign?.enabled && <Button
                onClick={() => navigate('/special-offers?campaign=hero')}
                variant="outline"
                data-testid="hero-inspection-btn"
                className="h-12 sm:h-14 px-7 text-base font-semibold rounded-full bg-transparent text-white border-white/30 hover:bg-white/10 hover:border-white/60 hover:text-white transition-colors"
              >
                {heroCopy.cta_secondary || campaign.button_label || 'Get Free Inspection'}
              </Button>}
              <a
                href={CALL_TEL}
                data-testid="hero-contact-btn"
                aria-label="Call EntoRid expert now"
                className="inline-flex items-center justify-center gap-2 h-12 sm:h-14 px-6 sm:px-7 text-base font-semibold rounded-full bg-white text-brand-blue-dark border border-white/80 shadow-lg shadow-black/10 transition-all duration-200 hover:bg-brand-green hover:text-white hover:border-brand-green hover:scale-[1.02] active:scale-[0.98]"
              >
                <span className="relative w-7 h-7 rounded-full bg-brand-green/15 inline-flex items-center justify-center">
                  <span className="absolute inset-0 rounded-full bg-brand-green/30 animate-ping" />
                  <Phone size={15} className="relative" />
                </span>
                <span>Contact Us</span>
              </a>
            </div>

            {theme?.banner?.enabled && theme.banner.text && <div className="mt-4 inline-flex items-center gap-2 border border-white/25 bg-white/10 px-3 py-2 text-sm font-semibold text-white backdrop-blur-sm" data-testid="theme-promo-banner"><Sparkles size={15} className="text-brand-green-light" />{theme.banner.text}</div>}

            {/* Hero promo coupon (Super Admin configurable) */}
            <HeroCouponPromo />

            {/* Trust stats — mobile-friendly 3 pills */}
            <div className="mt-7 grid grid-cols-3 gap-2 sm:gap-3 max-w-md mx-auto lg:mx-0" data-testid="hero-trust-badges">
              <div className="flex items-center gap-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 px-2.5 py-2">
                <Users size={16} className="text-brand-green-light flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-bold leading-none">10,000+</div>
                  <div className="text-[10px] text-white/70 leading-tight mt-0.5">Happy Customers</div>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 px-2.5 py-2">
                <Star size={16} className="text-yellow-300 fill-yellow-300 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-bold leading-none">4.9★</div>
                  <div className="text-[10px] text-white/70 leading-tight mt-0.5">Google Rating</div>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 px-2.5 py-2">
                <ShieldCheck size={16} className="text-brand-green-light flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-bold leading-none">Certified</div>
                  <div className="text-[10px] text-white/70 leading-tight mt-0.5">Experts</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

function SeasonalParticles({ type }) {
  return <div aria-hidden className={`seasonal-particles seasonal-${type}`}>{Array.from({ length: 18 }).map((_, index) => <span key={index} style={{ '--i': index }} />)}</div>;
}
